<template>
    <div :class="['product-configurator__option', 'form-field', {'order-last': isQuantityGroup}]">
        <div class="product-configurator__option-header">
            <label class="form-field__label title title--3">
                {{ name }}
            </label>
            <DynamicModalButton v-if="moreInfo?.description" class="product-more-info" aria-label="Click for more info">
                <template #button>
                    <svg xmlns="http://www.w3.org/2000/svg" height="22" viewBox="0 -960 960 960" width="22">
                        <title>More Info</title>
                        <path d="M450.001-290.001h59.998V-520h-59.998v229.999ZM480-588.461q13.731 0 23.019-9.288 9.288-9.288 9.288-23.019 0-13.73-9.288-23.019-9.288-9.288-23.019-9.288-13.731 0-23.019 9.288-9.288 9.289-9.288 23.019 0 13.731 9.288 23.019 9.288 9.288 23.019 9.288Zm.067 488.46q-78.836 0-148.204-29.92-69.369-29.92-120.682-81.21-51.314-51.291-81.247-120.629-29.933-69.337-29.933-148.173t29.92-148.204q29.92-69.369 81.21-120.682 51.291-51.314 120.629-81.247 69.337-29.933 148.173-29.933t148.204 29.92q69.369 29.92 120.682 81.21 51.314 51.291 81.247 120.629 29.933 69.337 29.933 148.173t-29.92 148.204q-29.92 69.369-81.21 120.682-51.291 51.314-120.629 81.247-69.337 29.933-148.173 29.933ZM480-160q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z"/>
                    </svg>
                </template>
                <template #modal>
                    <div v-html="moreInfo?.description" class="richtext"></div>
                </template>
            </DynamicModalButton>
        </div>
        <component 
            :is="fieldType"
            :model="model"
            @change="handleFieldChange($event, index)"
            :attributes="processedAttributes"
            :hide-disabled="hideDisabled"
        />
    </div>
</template>
<script>
import isEqual from 'lodash/isEqual';
import { defineAsyncComponent, computed } from 'vue'; 
import { useProductStore } from '../../stores/_product-store';
import { storeToRefs } from 'pinia';
import { ref } from 'vue';

export default {
    emits: ["change"],
    props: {
        model: {
            required: true
        },
        name: {
            type: String,
            required: true,
            default: 'Field Label'
        },
        moreInfo: {
            type: Object,
            default: () => {}
        },
        attributes: {
            type: Array,
            required: true,
            default: () => []
        },
        hideDisabled: {
            type: Boolean,
            default: false
        }
    },
    components: {
        Select: defineAsyncComponent(() => import(/* webpackChunkName: 'prod-select' */ './fields/SelectField.vue')),
        // CheckboxList: defineAsyncComponent(() => import(/* webpackChunkName: 'prod-check-list' */ './fields/CheckboxListField.vue')),
        Quantity: defineAsyncComponent(() => import(/* webpackChunkName: 'prod-quantity-list-field' */ './fields/QuantityListField.vue')),
        RadioList: defineAsyncComponent(() => import(/* webpackChunkName: 'prod-radio' */ './fields/RadioListField.vue')),
        ImageRadioList: defineAsyncComponent(() => import(/* webpackChunkName: 'prod-image-choice' */ './fields/ImageChoiceListField.vue')),
        Artwork: defineAsyncComponent(() => import(/* webpackChunkName: 'prod-artwork-field' */ './fields/ArtworkField.vue')),
    },
    setup(props, { emit }) {
        const store = useProductStore();
        const { name, moreInfo, attributes, hideDisabled } = props;
        const { currentProduct, currentProductData, quantity, variants, selectedAttributes} = storeToRefs(store);
        const hideQuantity = currentProductData.value.hideQuantity;
        const quantityAlias = currentProductData.value.quantityAlias;
        const attributeGroups = currentProductData.value.attributeGroups;
        const fieldType = props.moreInfo?.fieldType ? props.moreInfo.fieldType : 'radio-list';
        const currentModel = ref(props.model);

        const updateField = () => {
            emit('change', currentModel.value);
        };

        const handleFieldChange = (newValue) => {
            currentModel.value = newValue;
            updateField();
        };

        const isQuantityGroup = computed(() => {
            return fieldType === "quantity";
        });

        const processedAttributes = computed(() => {
            let proAttributes = [];

            attributes.forEach(option => {

                if (option.groupKey != null) {

                    let newAttributes = [...selectedAttributes.value];
                    newAttributes[option.groupKey] = option.alias;

                    let foundVariant = variants.value.find(v => {
                        return isEqual(v.attributeAliases, newAttributes);
                    });

                    if (foundVariant) {
                        let price = foundVariant.price - currentProduct.value.price;
                        option.price = price || 0;
                        option.isDisabled = false;
                    } else {
                        option.isDisabled = true;
                        option.price = 0;
                    }
                }

                if (option.multiplies && quantity.value > 0) {

                    if (hideQuantity && quantityAlias) {
                        //Find the selected quantity attribute and retrieve value

                        let quantityGroup = attributeGroups.find(x => {
                            return isEqual(x.alias, quantityAlias);
                        });

                        if (quantityGroup) {

                            let selectedAttribute = quantityGroup.attributes.find(x => {
                                return currentProduct.value.attributeAliases.includes(x.alias);
                            });

                            let attributeQuantity = Number(selectedAttribute.name);

                            let price = option.price * attributeQuantity;

                            if (option.minimumPrice > 0 && price < option.minimumPrice) {
                                option.syncedPrice = option.minimumPrice;
                            } else {
                                option.syncedPrice = price;
                            }

                            option.labelSuffix = attributeQuantity > 1 ? ` x${attributeQuantity}` : null;
                        } else {
                            //idk
                        }
                    } else {
                        option.syncedPrice = option.price * quantity.value;
                        option.labelSuffix = quantity.value > 1 ? ` x${quantity.value} ` : null;
                    }

                }

                proAttributes.push(option);
            });

            return proAttributes;
        });

        const processedName = computed(() => {
            return name.replace(/{productName}/g, currentProductData.value.name);
        });

        return {
            name: processedName,
            moreInfo,
            hideDisabled,
            processedAttributes,
            currentModel,
            updateField,
            fieldType,
            handleFieldChange,
            isQuantityGroup
        };
    }
}
</script>